import {URL} from "../constants/API";
import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {barberLogout, customerLogout, setCustomerTokens, setTokens} from "../slices/authSlice";
import {isFilled} from "../utils/generalHelper";
import {serializeParams} from "../utils/queryParams";
import {openLimitGate, type GateKey} from "../slices/limitGateSlice";
import {RootState} from "../store/store.ts";

// Map a mutation URL to the capability that owns it, so a 402 from any of
// these endpoints surfaces the right copy in the LimitGate modal. Keep this
// in sync with the audit table when new gated endpoints land.
const CAPABILITY_BY_URL: Array<[RegExp, GateKey]> = [
    [/^barber(\/|$)/, 'staff'],
    [/^vacation(\/|$)/, 'vacations'],
    [/^waitlist\//, 'waitlist'],
    [/^service(\/|$)/, 'multiServices'],
    [/^me\/barber-shop\/booking-settings/, 'whiteLabel'],
    [/^me\/barber-shop$/, 'customDomain'],
    [/^notification/, 'sms'],
]

const inferGateKey = (url: string | undefined): GateKey | null => {
    if (!url) return null
    for (const [re, key] of CAPABILITY_BY_URL) {
        if (re.test(url)) return key
    }
    return null
}

const config = {
    apiUrl: URL.concat('/api/')
}


const baseQuery = fetchBaseQuery({
    baseUrl: config.apiUrl,
    paramsSerializer: serializeParams,
});

// Helper to check if request is for customer portal
const isCustomerPortalRequest = (url: string | undefined): boolean => {
    return url?.startsWith('customer/portal') ?? false;
};

const baseAuthenticatedQuery = fetchBaseQuery({
    baseUrl: config.apiUrl,
    paramsSerializer: serializeParams,
    prepareHeaders: (headers, {getState}) => {
        const auth = (getState() as RootState).auth;
        // While impersonating, every barber-scoped call goes out as the target
        // user — the admin platform's own routes are unreachable in this state
        // because the BE's ROLE_ADMIN guard rejects the target's JWT. Admins
        // must "Stop impersonating" to come back to /admin/*.
        const token = auth.impersonationToken ?? auth.token;
        if (token) {
            headers.set('authorization', `Bearer ${token}`)
        }
        return headers
    },
});

const customerAuthenticatedQuery = fetchBaseQuery({
    baseUrl: config.apiUrl,
    prepareHeaders: (headers, {getState}) => {
        const customerToken = (getState() as RootState).auth.customerToken;
        if (customerToken) {
            headers.set('authorization', `Bearer ${customerToken}`)
        }
        return headers
    },
});

const baseQueryWithReauth = async (
    args: Parameters<typeof baseAuthenticatedQuery>[0],
    api: Parameters<typeof baseAuthenticatedQuery>[1],
    extraOptions: Parameters<typeof baseAuthenticatedQuery>[2]
) => {
    const url = typeof args === 'string' ? args : args?.url;
    const isCustomerRequest = isCustomerPortalRequest(url);
    const state = api.getState() as RootState;

    // Use appropriate query based on endpoint type
    const queryFn = isCustomerRequest ? customerAuthenticatedQuery : baseAuthenticatedQuery;
    let result = await queryFn(args, api, extraOptions);

    // Handle customer portal 401 → refresh
    if (isCustomerRequest) {
        const refreshToken = state.auth.customerRefreshToken;

        if (result.error && result.error.status === 401 && isFilled(refreshToken)) {
            const refreshResult = await baseQuery({
                url: 'customer/token/refresh',
                method: 'POST',
                headers: {},
                body: {refresh_token: refreshToken},
            }, api, extraOptions);

            if (refreshResult.data) {
                api.dispatch(setCustomerTokens(refreshResult.data as {token: string; refresh_token: string}));
                result = await customerAuthenticatedQuery(args, api, extraOptions);
            } else {
                api.dispatch(customerLogout());
            }
        }
        return result;
    }

    // Skip reauth for login endpoint
    if (url === 'login_check') {
        return result
    }

    // Handle barber 401 → refresh. Skipped while impersonating: the
    // impersonation JWT is short-lived (1h, no refresh endpoint), so a 401 on
    // an impersonated call means the session just timed out — surface it to
    // the page instead of clobbering the admin's own refresh token.
    if (state.auth.impersonationToken) {
        return result
    }
    const refreshToken = state.auth.refreshToken;

    if (result.error && result.error.status === 401 && isFilled(refreshToken)) {
        const refreshResult = await baseQuery({
            url: 'token/refresh',
            method: 'POST',
            headers: {},
            body: {refresh_token: refreshToken},
        }, api, extraOptions);

        if (refreshResult.data) {
            api.dispatch(setTokens(refreshResult.data as {token: string; refresh_token: string}));
            result = await baseAuthenticatedQuery(args, api, extraOptions);
        } else {
            api.dispatch(barberLogout());
        }
    }

    // 402 Payment Required → plan-limit safety net. Any mutation that
    // outruns the FE's proactive canUse() check still ends up here, so the
    // user gets the same upgrade modal instead of a silent failure.
    if (result.error && result.error.status === 402) {
        const data = (result.error.data ?? {}) as {
            capability?: GateKey
            message?: string
            suggestedPlan?: string
        }
        const inferredKey = data.capability ?? inferGateKey(url)
        if (inferredKey) {
            api.dispatch(openLimitGate({
                key: inferredKey,
                reason: typeof data.message === 'string' ? data.message : undefined,
                suggestedPlan: data.suggestedPlan,
            }))
        }
    }

    return result;
};

export const baseApi = createApi({
        baseQuery: baseQueryWithReauth,
        tagTypes: [
            'ReservationBarberShop',
            'CurrentUser',
            'LoyaltyConfig',
            'Reservations',
            'Customers',
            'Vacations',
            'Ratings',
            'CustomerPortal',
            'CustomerPortalReservations',
            'CustomerPortalReviews',
            'CustomerPortalStats',
            'CustomerPortalFavorites',
            'PublicBarberShops',
            'Subscription',
            'FreeTierUsage',
            'WaitlistConfig',
            'BarberWaitlist',
            'CustomerWaitlist',
            'Onboarding',
            'Services',
            'ServiceCategories',
            'MyBarberServices',
            'ShopHours',
            'MyHours',
            'Barbers',
            'Positions',
            'ShopBookingSettings',
            'MyBookingSettings',
            'NotificationsConfig',
            'NotificationsAudit',
            'Blocklist',
            'BlocklistDefaults',
            'AdminGlobalCustomers',
            'AdminDedupGroups',
            'Insights',
            'InsightActionFlows',
            'Tags',
            'CustomerTagAssignments',
            'PaymentsConnect',
            'PaymentsConfig',
            'TipContext',
            'BlogPosts',
            'BlogCategories',
        ],
        endpoints: () => ({}),
    }
)
